/**
 * Markdown simples (ver `markdownEmBlocos.js`) desenhado como elementos React.
 * Nada passa por `dangerouslySetInnerHTML`: o texto da IA é sempre texto.
 */
import React, { useMemo } from "react";
import styled from "styled-components";

import { lerMarkdown } from "./markdownEmBlocos";

const Corpo = styled.div`
  min-width: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink);
  overflow-wrap: anywhere;

  > * + * {
    margin-top: 10px;
  }

  p,
  ul,
  ol {
    margin: 0;
  }

  ul,
  ol {
    padding-left: 22px;
  }

  li + li,
  li > ul,
  li > ol {
    margin-top: 4px;
  }

  li::marker {
    color: var(--ink-3);
  }

  strong {
    font-weight: 600;
  }

  code {
    font-family: var(--font-mono);
    font-size: 0.88em;
    padding: 1px 5px;
    border-radius: 5px;
    background: var(--surface-2);
  }

  h3,
  h4,
  h5 {
    margin: 16px 0 0;
    line-height: 1.3;
    font-weight: 700;
    color: var(--ink);
    text-wrap: balance;
  }

  > h3:first-child,
  > h4:first-child,
  > h5:first-child {
    margin-top: 0;
  }

  h3 {
    font-size: 16px;
  }

  h4 {
    font-size: 15px;
  }

  h5 {
    font-size: 14px;
  }
`;

const TAG_DO_TITULO = { 1: "h3", 2: "h4", 3: "h5" };

function Trechos({ trechos }) {
  return trechos.map((trecho, i) => {
    // Os trechos de uma linha nunca mudam de ordem: o índice é estável.
    if (trecho.tipo === "negrito") return <strong key={i}><Trechos trechos={trecho.filhos} /></strong>;
    if (trecho.tipo === "italico") return <em key={i}><Trechos trechos={trecho.filhos} /></em>;
    if (trecho.tipo === "codigo") return <code key={i}>{trecho.texto}</code>;
    return <React.Fragment key={i}>{trecho.texto}</React.Fragment>;
  });
}

function Linhas({ linhas }) {
  return linhas.map((trechos, i) => (
    <React.Fragment key={i}>
      {i > 0 ? <br /> : null}
      <Trechos trechos={trechos} />
    </React.Fragment>
  ));
}

function Lista({ lista }) {
  const Tag = lista.ordenada ? "ol" : "ul";
  return (
    <Tag start={lista.ordenada && lista.inicio !== 1 ? lista.inicio : undefined}>
      {lista.itens.map((item, i) => (
        <li key={i}>
          <Linhas linhas={item.trechos} />
          {item.sublista ? <Lista lista={item.sublista} /> : null}
        </li>
      ))}
    </Tag>
  );
}

function Bloco({ bloco }) {
  if (bloco.tipo === "titulo") {
    const Tag = TAG_DO_TITULO[bloco.nivel];
    return <Tag><Trechos trechos={bloco.trechos} /></Tag>;
  }
  if (bloco.tipo === "lista") return <Lista lista={bloco} />;
  return <p><Linhas linhas={bloco.linhas} /></p>;
}

export default function Markdown({ texto, className }) {
  const blocos = useMemo(() => lerMarkdown(texto), [texto]);
  return (
    <Corpo className={className}>
      {blocos.map((bloco, i) => <Bloco key={i} bloco={bloco} />)}
    </Corpo>
  );
}
